import { z } from "zod";
import type {
  ReasoningApi,
  ReasoningEvidence,
  ReasoningRecord,
  ReasoningSession,
} from "~/api/reasoningTypes";

const evidenceSchema = z
  .object({
    sourceKind: z.string().min(1),
    sourceId: z.string().min(1),
    revision: z.string().min(1),
    contentDigest: z.string().regex(/^[a-fA-F0-9]{64}$/),
    observedEpoch: z.number().int().nonnegative().optional(),
    expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
  })
  .strict();

// Only authorable values enter JSON. Session identity, revision and context are pinned separately.
export const candidateDraftSchema = z
  .object({
    schemaVersion: z.literal("reasoning-candidate-draft/1"),
    logicalId: z.string(),
    content: z.string(),
    rationale: z.string(),
    evidenceComplete: z.boolean(),
    derivedFromRecordIds: z.array(z.string()),
    evidence: z.array(evidenceSchema),
  })
  .strict();
export type CandidateDraft = z.infer<typeof candidateDraftSchema>;
export type CandidateCommand = Parameters<ReasoningApi["candidate"]>[0];
export interface CandidateBase {
  session: ReasoningSession;
  predecessorId: string | null;
}

/** Source eligibility can change without a session command advancing its revision. */
export function sessionSnapshotKey(session: ReasoningSession) {
  return JSON.stringify([
    session.id,
    session.revision,
    session.contextRecordId,
    session.dependencyEpoch,
    session.criteriaDigest,
    session.currentAcceptanceRecordId,
    session.state,
    session.deadline,
    session.records.map((record) => [record.id, record.eligible, record.ineligibilityReason]),
  ]);
}

export function candidateRecords(session: ReasoningSession) {
  return session.records.filter((record) => record.kind === "candidate");
}
export function latestCandidate(session: ReasoningSession, logicalId: string) {
  return candidateRecords(session)
    .filter((record) => record.logicalId === logicalId)
    .sort((a, b) => b.revision - a.revision)[0];
}
export function pinnedEvidence(session: ReasoningSession): ReasoningEvidence[] {
  return readEvidence(session.records.find((record) => record.id === session.contextRecordId));
}
function readEvidence(record?: ReasoningRecord): ReasoningEvidence[] {
  const result = z.array(evidenceSchema).safeParse(record?.payload.evidence ?? []);
  return result.success ? result.data : [];
}
function parentIds(record?: ReasoningRecord): string[] {
  const result = z.array(z.string()).safeParse(record?.payload.derivedFromRecordIds ?? []);
  return result.success ? result.data : [];
}
export function draftFrom(base: CandidateBase): CandidateDraft {
  const record = base.session.records.find((item) => item.id === base.predecessorId);
  let name = "answer";
  for (let n = 2; latestCandidate(base.session, name); n++) name = `answer-${n}`;
  return {
    schemaVersion: "reasoning-candidate-draft/1",
    logicalId: record?.logicalId ?? name,
    content: typeof record?.payload.content === "string" ? record.payload.content : "",
    rationale: typeof record?.payload.rationale === "string" ? record.payload.rationale : "",
    evidenceComplete: record?.payload.evidenceComplete !== false,
    evidence: record ? readEvidence(record) : pinnedEvidence(base.session),
    derivedFromRecordIds: [...new Set([...(record ? [record.id] : []), ...parentIds(record)])],
  };
}
export const draftJson = (draft: CandidateDraft) => JSON.stringify(draft, null, 2);
export function parseDraft(
  text: string,
): { draft: CandidateDraft; errors: [] } | { draft: null; errors: string[] } {
  try {
    const result = candidateDraftSchema.safeParse(JSON.parse(text));
    return result.success
      ? { draft: result.data, errors: [] }
      : {
          draft: null,
          errors: result.error.issues.map(
            (issue) => `${issue.path.join(".") || "Draft"}: ${issue.message}`,
          ),
        };
  } catch {
    return {
      draft: null,
      errors: ["JSON is incomplete or invalid. Fix it or restore the last valid draft."],
    };
  }
}
export const evidenceKey = (e: ReasoningEvidence) =>
  JSON.stringify([
    e.sourceKind,
    e.sourceId,
    e.revision,
    e.contentDigest,
    e.observedEpoch ?? 0,
    e.expiresAt ?? null,
  ]);
export const evidenceLabel = (e: ReasoningEvidence) =>
  `${e.sourceKind}:${e.sourceId} · ${e.revision}`;
export function nextRevision(base: CandidateBase, draft: CandidateDraft) {
  return (latestCandidate(base.session, draft.logicalId)?.revision ?? 0) + 1;
}

/**
 * The caps a draft is checked against before it is sent. The record byte cap and the branch
 * count come from `/api/reasoning.support`; the parent and name caps are not published by the
 * engine and are named once in `useReasoningSupport.ts`.
 */
export interface DraftLimits {
  maxRecordBytes: number;
  maxCandidateParents: number;
  maxLogicalIdLength: number;
}
/** Mirrors `REASONING_FALLBACK_LIMITS` (useReasoningSupport.ts) for callers without the hook. */
export const DEFAULT_DRAFT_LIMITS: DraftLimits = {
  maxRecordBytes: 256 * 1024,
  maxCandidateParents: 128,
  maxLogicalIdLength: 160,
};

export function validateDraft(
  base: CandidateBase,
  draft: CandidateDraft,
  now = Date.now(),
  limits: DraftLimits = DEFAULT_DRAFT_LIMITS,
): string[] {
  const { session, predecessorId } = base;
  const errors: string[] = [];
  const context = session.records.find((row) => row.id === session.contextRecordId);
  if (!context?.eligible)
    errors.push("Current context is unavailable or stale. Refresh its evidence before saving.");
  if (session.state === "cancelled") errors.push("This reasoning session is cancelled.");
  if (session.deadline && Date.parse(session.deadline) <= now)
    errors.push("This session's deadline has expired.");
  if (
    !draft.logicalId.trim() ||
    draft.logicalId.length > limits.maxLogicalIdLength ||
    draft.logicalId !== draft.logicalId.trim()
  )
    errors.push(
      `Use a candidate name of 1–${limits.maxLogicalIdLength} characters without leading or trailing spaces.`,
    );
  if (!draft.content.trim()) errors.push("Candidate content is required.");
  const predecessor = session.records.find((row) => row.id === predecessorId);
  const latest = latestCandidate(session, draft.logicalId);
  if (predecessorId) {
    if (!predecessor || predecessor.logicalId !== draft.logicalId)
      errors.push("A revision must keep its predecessor's candidate name.");
    if (latest?.id !== predecessorId)
      errors.push("This candidate has a newer revision. Review the latest revision before saving.");
    if (!draft.derivedFromRecordIds.includes(predecessorId))
      errors.push("Keep the predecessor dependency when saving a revision.");
  } else if (latest)
    errors.push("That candidate already exists. Choose it to revise, or use a new name.");
  if (
    draft.derivedFromRecordIds.length > limits.maxCandidateParents ||
    draft.evidence.length > limits.maxCandidateParents
  )
    errors.push(
      `At most ${limits.maxCandidateParents} parents and ${limits.maxCandidateParents} evidence references are supported.`,
    );
  if (new Set(draft.derivedFromRecordIds).size !== draft.derivedFromRecordIds.length)
    errors.push("Parent dependencies must be unique.");
  for (const id of new Set(draft.derivedFromRecordIds)) {
    const record = session.records.find((row) => row.id === id);
    if (!record || !["candidate", "retained-artifact"].includes(record.kind))
      errors.push(`Parent ${id} must be a candidate or retained file in this session.`);
    else if (!record.eligible)
      errors.push(
        `Parent ${record.logicalId} r${record.revision} is stale. Start a new candidate using current evidence or remove this optional parent.`,
      );
  }
  const pinned = new Set(pinnedEvidence(session).map(evidenceKey));
  if (new Set(draft.evidence.map(evidenceKey)).size !== draft.evidence.length)
    errors.push("Evidence references must be unique.");
  for (const evidence of draft.evidence) {
    if (!pinned.has(evidenceKey(evidence)))
      errors.push(`Evidence ${evidenceLabel(evidence)} is not pinned in the current context.`);
    if (evidence.expiresAt && Date.parse(evidence.expiresAt) <= now)
      errors.push(`Evidence ${evidenceLabel(evidence)} has expired.`);
  }
  // The server measures its canonical record, including internal metadata, and enforces the final cap.
  if (new TextEncoder().encode(JSON.stringify(draft)).length > limits.maxRecordBytes - 1024)
    errors.push(
      `Draft is too large for the ${Math.round(limits.maxRecordBytes / 1024)} KiB record limit. Reduce its retained content.`,
    );
  return errors;
}

export function candidateCommand(
  base: CandidateBase,
  draft: CandidateDraft,
  commandId: string,
): CandidateCommand {
  const { schemaVersion: _, ...fields } = draft;
  return {
    ...fields,
    sessionId: base.session.id,
    expectedSessionRevision: base.session.revision,
    revision: nextRevision(base, draft),
    commandId,
  };
}

/** User-triggered rebase keeps local text and exact evidence. Changed source versions need review. */
export function rebaseDraft(
  draft: CandidateDraft,
  latest: ReasoningSession,
): { base: CandidateBase; draft: CandidateDraft } {
  const predecessor = latestCandidate(latest, draft.logicalId);
  return {
    base: { session: latest, predecessorId: predecessor?.id ?? null },
    draft: {
      ...draft,
      derivedFromRecordIds: [
        ...new Set([...(predecessor ? [predecessor.id] : []), ...draft.derivedFromRecordIds]),
      ],
    },
  };
}

interface Dependency {
  key: string;
  label: string;
}
function dependencies(
  session: ReasoningSession,
  contextId: string,
  parents: string[],
  evidence: ReasoningEvidence[],
): Dependency[] {
  return [
    { key: `context:${contextId}`, label: `Context ${contextId}` },
    ...parents.map((id) => {
      const row = session.records.find((r) => r.id === id);
      return {
        key: `parent:${id}`,
        label: row ? `${row.logicalId} · r${row.revision}` : `Unknown parent ${id}`,
      };
    }),
    ...evidence.map((e) => ({ key: `evidence:${evidenceKey(e)}`, label: evidenceLabel(e) })),
  ];
}
export function dependencyDiff(base: CandidateBase, draft: CandidateDraft) {
  const original = base.session.records.find((row) => row.id === base.predecessorId);
  const before = original
    ? dependencies(
        base.session,
        String(original.payload.contextRecordId ?? ""),
        parentIds(original),
        readEvidence(original),
      )
    : [];
  const after = dependencies(
    base.session,
    base.session.contextRecordId,
    draft.derivedFromRecordIds,
    draft.evidence,
  );
  const beforeKeys = new Set(before.map((item) => item.key));
  const afterKeys = new Set(after.map((item) => item.key));
  return [
    ...after.map((item) => ({ ...item, change: beforeKeys.has(item.key) ? "Kept" : "Added" })),
    ...before
      .filter((item) => !afterKeys.has(item.key))
      .map((item) => ({ ...item, change: "Removed" })),
  ];
}
